import { SerializedConstructor } from '@langchain/core/load/serializable';
import { LinearProgress } from '@mui/joy';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAlignEvaluatorInfo } from '@/Pages/Playground/PlaygroundHome/hooks/useAlignEvaluator';
import Breadcrumbs from '@/components/Breadcrumbs';
import { EvaluatorStoreProvider } from '@/components/EvaluatorCrudPane/store/EvaluatorStoreProvider';
import { useCommit, useRepo, useRunRules } from '@/hooks/useSwr';
import {
  CommitManifestResponse,
  EPromptType,
  RuleSchema,
} from '@/types/schema';

import { PlaygroundStoreContext } from '../Playground/PlaygroundHome/PlaygroundStoreContext';
import { storedManifestToPlaygroundManifestAndOptions } from '../Playground/utils/Playground.utils';
import { EvaluatorPlaygroundMain } from './EvaluatorPlaygroundMain';

function Create() {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    alignmentDatasetId,
    isLoading: isLoadingAlignEvaluatorInfo,
    hubRef: evaluatorHubRef,
    commitHash: evaluatorCommitHash,
    evaluatorId,
    mutateRunRules: mutateRunRulesAlignEvaluatorInfo,
  } = useAlignEvaluatorInfo();

  const repo = useRepo(undefined, evaluatorHubRef ?? '');
  const commit = useCommit({
    owner: undefined,
    repo: evaluatorHubRef,
    sha: evaluatorCommitHash,
    get_examples: true,
    include_model: true,
  });
  const {
    data: evaluators,
    isLoading: _isLoadingEvaluators,
    mutate: mutateRunRules,
  } = useRunRules(evaluatorId ? { id: [evaluatorId] } : undefined);
  const evaluator = evaluators?.[0];
  const isLoadingEvaluators = _isLoadingEvaluators || !evaluator;

  const manifest: SerializedConstructor | undefined = useMemo(() => {
    return commit.data?.manifest;
  }, [commit.data]);

  const [shouldReset] = useState(searchParams.get('reset') ?? undefined);

  const onSaveEvaluator = useCallback(
    (
      newCommit: CommitManifestResponse | undefined,
      newRule: RuleSchema | undefined
    ) => {
      if (newRule) {
        mutateRunRules([newRule]);
        mutateRunRulesAlignEvaluatorInfo([newRule]);
      }
      if (newCommit) {
        if (repo.data) {
          repo.mutate({
            repo: {
              ...repo.data?.repo,
              last_commit_hash: newCommit.commit_hash,
            },
          });
        }
        commit.mutate(newCommit, { revalidate: false });
      }
    },
    [mutateRunRules, repo, commit, mutateRunRulesAlignEvaluatorInfo]
  );

  useEffect(() => {
    if (shouldReset) {
      setSearchParams((prev) => {
        prev.delete('reset');
        return prev;
      });
    }
  }, []);

  if (
    !commit ||
    !manifest ||
    isLoadingAlignEvaluatorInfo ||
    !evaluatorHubRef ||
    !repo.data ||
    isLoadingEvaluators
  ) {
    return (
      <div className="mx-2 mt-4">
        <LinearProgress />
      </div>
    );
  }

  const { promptManifest, modelManifest, options, isChat } =
    storedManifestToPlaygroundManifestAndOptions(manifest);

  const lastCommitHash = repo.data?.repo.last_commit_hash;

  return (
    <EvaluatorStoreProvider storeType="alignPlayground">
      <PlaygroundStoreContext.Provider value="alignment">
        <EvaluatorPlaygroundMain
          datasetId={alignmentDatasetId}
          forceReset={!!shouldReset}
          promptManifest={promptManifest}
          modelManifest={modelManifest}
          type={isChat ? EPromptType.CHAT : EPromptType.INSTRUCT}
          options={options}
          repo={repo.data.repo}
          commitHash={
            evaluatorCommitHash === 'latest' && lastCommitHash
              ? lastCommitHash
              : evaluatorCommitHash
          }
          showChatCompletionToggle
          disabledCompare
          evaluator={evaluator}
          onSaveEvaluator={onSaveEvaluator}
        />
      </PlaygroundStoreContext.Provider>
    </EvaluatorStoreProvider>
  );
}

export function EvaluatorPlaygroundPage() {
  return (
    <div className="flex h-screen flex-col">
      <div className="px-4 pt-3">
        <Breadcrumbs />
      </div>
      <Create />
    </div>
  );
}
